<!-- src/components/Profile/ChangePasswordForm.vue -->
<script setup>
import { ref, computed, reactive, onUnmounted, defineExpose } from "vue";
import { useUserStore } from "@/stores/useUserStore";

const userStore = useUserStore();

const currentPassword = ref("");

// Objeto reativo para nova senha + confirmação
const passwordForm = reactive({
  newPassword: "",
  confirmNewPassword: "",
});

// Visibilidade dos campos
const showCurrent = ref(false);
const showNew = ref(false);
const showConfirm = ref(false);

const isSubmitting = ref(false);

// 🔹 PATCH: usar o campo `hasPassword` que vem do backend (UserReadDto.HasPassword)
//           Em JS ele chega como `hasPassword`.
const hasLocalPassword = computed(() => {
  return !!userStore.currentUser?.hasPassword;
});

// Toast local
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error"); // 'error' | 'success'
let toastTimeoutId = null;

function resetToastTimeout(duration) {
  return new Promise((resolve) => {
    if (toastTimeoutId) clearTimeout(toastTimeoutId);
    toastTimeoutId = setTimeout(() => {
      showToast.value = false;
      toastTimeoutId = null;
      resolve();
    }, duration);
  });
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

async function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  await resetToastTimeout(2400);
}

onUnmounted(() => {
  if (toastTimeoutId) clearTimeout(toastTimeoutId);
});

// Critérios da nova senha
const passwordCriteriaMet = computed(() => {
  const password = passwordForm.newPassword || "";
  return {
    length: password.length >= 8,
    letter: /[a-zA-Z]/.test(password),
    number: /\d/.test(password),
  };
});

const passwordsMismatch = computed(() => {
  const p = passwordForm.newPassword?.trim();
  const c = passwordForm.confirmNewPassword?.trim();
  return p && c && p !== c;
});

function validateForm() {
  const cp = currentPassword.value?.trim();
  const np = passwordForm.newPassword?.trim();
  const cf = passwordForm.confirmNewPassword?.trim();

  // 🔹 Se o usuário TEM senha local, senha atual é obrigatória
  if (hasLocalPassword.value && !cp) {
    showErrorToast("Informe sua senha atual.");
    return false;
  }

  if (!np || !cf) {
    showErrorToast("Preencha todos os campos de senha.");
    return false;
  }

  if (np !== cf) {
    showErrorToast("A nova senha e a confirmação não coincidem.");
    return false;
  }

  const criteria = passwordCriteriaMet.value;
  if (!(criteria.length && criteria.letter && criteria.number)) {
    showErrorToast("A nova senha não atende aos requisitos de segurança.");
    return false;
  }

  return true;
}

async function handleChangePassword() {
  if (!validateForm()) return;

  isSubmitting.value = true;

  try {
    const userId = userStore.currentUser?.userId;

    if (!userId) {
      throw new Error("Usuário inválido para alteração de senha.");
    }

    // 🔹 Se não tem senha local, enviamos currentPassword vazio.
    //    A validação condicional é feita no backend.
    const payload = {
      currentPassword: hasLocalPassword.value
        ? currentPassword.value.trim()
        : "",
      newPassword: passwordForm.newPassword.trim(),
      confirmNewPassword: passwordForm.confirmNewPassword.trim(),
    };

    await userStore.changePassword(payload);

    // 🔹 PATCH: se foi a primeira senha criada, marca localmente `hasPassword = true`
    if (!hasLocalPassword.value && userStore.currentUser) {
      userStore.currentUser = {
        ...userStore.currentUser,
        hasPassword: true, // <-- aqui é o campo certo
      };
    }

    await showSuccessToast(
      hasLocalPassword.value
        ? "Sua senha foi alterada com sucesso."
        : "Sua senha foi criada com sucesso."
    );

    // Limpa campos
    currentPassword.value = "";
    passwordForm.newPassword = "";
    passwordForm.confirmNewPassword = "";
  } catch (error) {
    console.error("Erro ao alterar senha:", error);
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "Não foi possível alterar a senha. Tente novamente.";
    showErrorToast(msg);
  } finally {
    isSubmitting.value = false;
  }
}

// 👇 expõe o método para o componente pai (passwordFormRef.submit())
defineExpose({
  submit: handleChangePassword,
});
</script>

<template>
  <div class="form-container">
    <h3 class="view-title">
      {{ hasLocalPassword ? "Alterar senha" : "Criar senha de acesso" }}
    </h3>
    <p class="view-subtitle">
      {{
        hasLocalPassword
          ? "Altere sua senha informando a senha atual e definindo uma nova senha segura."
          : "Crie uma senha para usar login com e-mail e senha, além do login via Google."
      }}
    </p>

    <!-- Enter dentro dos campos ainda dispara a submissão -->
    <form @submit.prevent="handleChangePassword">
      <!-- Senha atual (somente se o usuário tiver senha local) -->
      <div class="form-group" v-if="hasLocalPassword">
        <label for="currentPassword">Senha atual</label>
        <div class="input-wrapper">
          <input
            id="currentPassword"
            :type="showCurrent ? 'text' : 'password'"
            v-model="currentPassword"
            class="input-base password-field"
            placeholder="Digite sua senha atual"
          />
          <button
            type="button"
            class="toggle-visibility-btn"
            @click="showCurrent = !showCurrent"
          >
            <i :class="showCurrent ? 'far fa-eye-slash' : 'far fa-eye'"></i>
          </button>
        </div>
      </div>

      <!-- Nova senha + Confirmar lado a lado -->
      <div class="form-group-row">
        <!-- Nova senha / Criar senha -->
        <div class="form-group flex-1">
          <label for="newPassword">
            {{ hasLocalPassword ? "Nova senha" : "Criar senha" }}
          </label>
          <div class="input-wrapper">
            <input
              id="newPassword"
              :type="showNew ? 'text' : 'password'"
              v-model="passwordForm.newPassword"
              class="input-base password-field"
              :class="{ 'input-error': passwordsMismatch }"
              :placeholder="
                hasLocalPassword
                  ? 'Digite a nova senha'
                  : 'Crie uma senha de acesso'
              "
            />
            <button
              type="button"
              class="toggle-visibility-btn"
              @click="showNew = !showNew"
            >
              <i :class="showNew ? 'far fa-eye-slash' : 'far fa-eye'"></i>
            </button>
          </div>

          <div class="password-hints" aria-live="polite">
            Use
            <span :class="{ met: passwordCriteriaMet.length }">8+ carac</span>,
            <span :class="{ met: passwordCriteriaMet.letter }">letras</span>,
            <span :class="{ met: passwordCriteriaMet.number }">números</span>.
          </div>
        </div>

        <!-- Confirmar nova senha / Confirmar senha -->
        <div class="form-group flex-1">
          <label for="confirmPassword">
            {{
              hasLocalPassword ? "Confirmar nova senha" : "Confirmar senha"
            }}
          </label>
          <div class="input-wrapper">
            <input
              id="confirmPassword"
              :type="showConfirm ? 'text' : 'password'"
              v-model="passwordForm.confirmNewPassword"
              class="input-base password-field"
              :class="{ 'input-error': passwordsMismatch }"
              :placeholder="
                hasLocalPassword
                  ? 'Repita a nova senha'
                  : 'Repita a senha criada'
              "
            />
            <button
              type="button"
              class="toggle-visibility-btn"
              @click="showConfirm = !showConfirm"
            >
              <i :class="showConfirm ? 'far fa-eye-slash' : 'far fa-eye'"></i>
            </button>
          </div>

          <p v-if="passwordsMismatch" class="error-text">
            As senhas não coincidem.
          </p>
        </div>
      </div>

      <!-- 👇 Botão interno REMOVIDO: quem salva é o footer do modal pai -->
    </form>

    <!-- Toast -->
    <Teleport to="body">
      <transition name="toast-fade">
        <div
          v-if="showToast"
          class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
        >
          <div
            :class="[
 'toast pointer-events-auto',
              toastType === 'error' ? 'toast-error' : 'toast-success',
            ]"
          >
            <div class="toast-icon">
              <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
                <path
                  v-if="toastType === 'error'"
                  d="M10 5v6M10 13.5v1.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                />
                <path
                  v-else
                  d="M7.5 10.5 9.5 12.5 13 8.5"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div class="toast-content">
              <p class="toast-title">
                {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
              </p>
              <p class="toast-message">{{ toastMessage }}</p>
            </div>

            <button
              type="button"
              class="toast-close hover:bg-black/10 rounded-full p-1 transition"
              @click="showToast = false"
              aria-label="Fechar notificação"
            >
              x
            </button>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style scoped>
.form-container {
  padding: 0.3rem;
  height: 100%;
  width: 100%;
  max-width: 620px;
  margin: 0 ;
}

.view-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.view-subtitle {
  font-size: 0.9rem;
  color: var(--text-light);
  margin-top: 0.2rem;
  margin-bottom: 1.2rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group-row {
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
}

.flex-1 {
  flex: 1;
  min-width: 220px;
}

.form-group label {
  margin-bottom: 0.35rem;
}

/* mesmo tamanho para todos os campos de senha */
.password-field {
  max-width: 220px;
  width: 100%;
}

/* Wrapper */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 220px;
}

/* Botão do olho */
.toggle-visibility-btn {
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text-light);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

/* Validação */
.input-error {
  border-color: var(--danger) !important;
}

.error-text {
  color: #ff4c4c;
  margin-top: 0.4rem;
  font-size: 0.8rem;
}

/* Hints da senha */
.password-hints {
  margin-top: 0.35rem;
  font-size: 0.82rem;
  color: var(--text-light);
}

.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

</style>
